export interface ClickUpCustomField {
  id: string;
  value: string | number;
}

export interface CreateClickUpTaskParams {
  /** ID da lista do ClickUp onde a tarefa será criada */
  listId: string;
  /** Nome da tarefa */
  name: string;
  /** Descrição da tarefa */
  description?: string;
  /** Status da tarefa (string com o nome do status, ex: "TODO", "IN PROGRESS") */
  status?: string;
  /** IDs dos usuários atribuídos à tarefa */
  assignees?: string[];
  /** Campos customizados */
  customFields?: ClickUpCustomField[];
}

export interface ClickUpTaskResponse {
  id: string;
  name: string;
  status: {
    status: string;
    color: string;
    type: string;
    orderindex: number;
  };
  url: string;
}

export async function createClickUpTask(
  params: CreateClickUpTaskParams,
): Promise<ClickUpTaskResponse> {
  const { listId, name, description, status, assignees, customFields } = params;

  if (!listId?.trim()) {
    throw new Error("listId is required");
  }
  if (!name?.trim()) {
    throw new Error("name is required");
  }

  const apiToken = import.meta.env.VITE_CLICKUP_API_TOKEN as unknown as string;

  if (!apiToken) {
    throw new Error("Token is not configured");
  }

  const requestBody: {
    name: string;
    description?: string;
    status?: string;
    assignees?: string[];
    custom_fields?: ClickUpCustomField[];
  } = {
    name: name.trim(),
  };

  if (description) {
    requestBody.description = description;
  }

  if (status) {
    requestBody.status = status;
  }

  if (assignees && assignees.length > 0) {
    requestBody.assignees = assignees;
  }

  if (customFields && customFields.length > 0) {
    requestBody.custom_fields = customFields;
  }

  try {
    const response = await fetch(
      `https://api.clickup.com/api/v2/list/${listId}/task`,
      {
        method: "POST",
        headers: {
          Authorization: apiToken,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      },
    );

    if (!response.ok) {
      let errorMessage = `Error creating task in ClickUp: ${response.status}`;

      try {
        const errorData = await response.json();
        errorMessage += ` - ${errorData.err || errorData.message || JSON.stringify(errorData)}`;
      } catch {
        const errorText = await response.text();
        errorMessage += ` - ${errorText || "Unknown error"}`;
      }

      throw new Error(errorMessage);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error(
      `Unexpected error creating task in ClickUp: ${String(error)}`,
    );
  }
}
